import React, { FC, useState, useMemo, useRef, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack, { StackProps } from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import {
  DataGridPro,
  DataGridProProps,
  GridActionsCellItem,
  GridColDef,
  GridRowClassNameParams,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridValidRowModel,
  LoadingOverlayPropsOverrides,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import {
  AddIcon,
  ChevronFilledDownIcon,
  ChevronFilledUpIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  EyeIcon,
  EyeSlashIcon,
  GenericDataGridStyle,
  MoreHorizontalIcon,
  ResetIcon,
} from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import { StrictUnion } from 'src/shared/utils';

import { DataGridBaseSelect } from './DataGridBaseSelect';
import { DataGridLoadingOverlay } from './DataGridLoadingOverlay';
import { DataGridNoRowsOverlay } from './DataGridNoRowsOverlay';

const DEFAULT_COLUMN_SETTINGS = { sortable: false };
const GRID_ROW_HEIGHT = 36;

interface ReadOnlyDataGridProps<T extends GridValidRowModel>
  extends Omit<DataGridProProps<T>, 'processRowUpdate' | 'rowModesModel'> {
  selectedRowId?: GridRowId;
}

interface CrudDataGridProps<T extends GridValidRowModel>
  extends Omit<DataGridProProps<T>, 'processRowUpdate' | 'rowModesModel'> {
  selectedRowId?: GridRowId;
  readOnly?: boolean;
  hasEdit?: boolean;
  onEditFocusField?: string;
  addLabel?: string;
  updating?: boolean;
  onUpdate?: (newRow: T) => Promise<T | undefined>;
  onDelete?: (params: GridRowParams) => void;
  onReset?: (params: GridRowParams) => void;
  onDownload?: (params: GridRowParams) => void;
  onHide?: (params: GridRowParams) => void;
  onView?: (params: GridRowParams) => void;
  onAdd?: () => T;
  sx?: DataGridProProps['sx'];
  _container?: StackProps['sx'];
}

type Props<T extends GridValidRowModel> = StrictUnion<ReadOnlyDataGridProps<T> | CrudDataGridProps<T>>;

export const DataGridCrud = <T extends GridValidRowModel>({
  // DataGrid props
  columns,
  rows: initialRows,
  loading,
  getRowId,
  slots,
  slotProps,
  getRowClassName,

  // Custom props
  selectedRowId,
  readOnly,
  hasEdit,
  onEditFocusField,
  addLabel,
  updating,
  onUpdate,
  onDelete,
  onReset,
  onDownload,
  onHide,
  onView,
  onAdd,
  sx,
  _container,
  ...props
}: Props<T>) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState(initialRows);

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [editingRowId, setEditingRowId] = useState<GridRowId>();
  const gridRef = useRef<HTMLDivElement | null>(null);

  const [isAdded, setIsAdded] = useState(false);
  const [shouldScrollDown, setShouldScrollDown] = useState(false);

  const gridColumns: GridColDef<T>[] = useMemo(
    () => columns.map((column) => ({ ...DEFAULT_COLUMN_SETTINGS, ...column })),
    [columns],
  );

  const hasActions = !readOnly && (hasEdit || !!onDelete || !!onReset || !!onDownload || !!onHide || !!onView);

  /**
   * Two useEffects in series are used here to scroll to new added rows
   * onAdd will update rows, rowModesModel and isAdded states but calling the grid's apiRef scroll method immediately after will fail as the rows will not yet exist in the grid
   * we need to wait one render (using shouldScrollDown state) before calling the scroll method
   */

  useEffect(() => {
    if (!isAdded) {
      return;
    }

    setShouldScrollDown(true);
    setIsAdded(false);
  }, [isAdded]);

  useEffect(() => {
    if (!shouldScrollDown) {
      return;
    }

    apiRef?.current?.scrollToIndexes({ rowIndex: initialRows.length - 1 });

    setShouldScrollDown(false);
  }, [shouldScrollDown, initialRows]);

  // Remove new rows if not added successfully
  const removeUnsavedRows = () => {
    if (rows.some((row) => +(getRowId?.(row) ?? row.id) < 0)) {
      setRows(cloneDeep(initialRows).filter((row) => +(getRowId?.(row) ?? row.id) >= 0));
    }
  };

  const handleEdit = (id: GridRowId) => {
    removeUnsavedRows();

    setRowModesModel((prevState) => editRowModesModel(prevState, id, onEditFocusField));

    setEditingRowId(id);
  };

  const handleSave = () => {
    setRowModesModel(saveRowModesModel);
  };

  const handleCancel = () => {
    removeUnsavedRows();

    setRowModesModel(cancelRowModesModel);

    setEditingRowId(undefined);
  };

  const handleAdd = () => {
    if (!onAdd) {
      return;
    }

    const newRow = onAdd();

    const newRowId = getRowId?.(newRow) ?? newRow.id;

    setRows((prevState) => [...cloneDeep(prevState), newRow]);

    handleEdit(newRowId);

    setIsAdded(true);
  };

  const processRowUpdate = async (newRow: T, oldRow: T) => {
    const newRowId = getRowId?.(newRow) ?? newRow.id;

    // Avoid sending unecessary API calls
    if (+newRowId >= 0 && isEqual(newRow, oldRow)) {
      setEditingRowId(undefined);
      return oldRow;
    }

    // MUI recommends calling the API inside processRowUpdate
    // Reference: https://mui.com/x/react-data-grid/editing/#server-side-persistence
    if (onUpdate) {
      const updatedRow = await onUpdate(newRow);

      if (!updatedRow) {
        throw new Error();
      }

      setEditingRowId(undefined);
      removeUnsavedRows();
      return updatedRow;
    }

    return newRow;
  };

  const handleProcessRowUpdateError = () => {
    // Catches error thrown from processRowUpdate and keeps grid in edit mode
    // Removing this prop will cause a MUI error in console
  };

  useEffect(() => {
    handleCancel();

    setRows(initialRows);
  }, [initialRows]);

  const actionsColumn: GridColDef<T> | undefined = hasActions
    ? ({
        ...DEFAULT_COLUMN_SETTINGS,
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        align: 'left',
        headerAlign: 'left',
        width: 80,
        getActions: (params: GridRowParams) =>
          params.id !== editingRowId
            ? [
                onDownload && (
                  <GridActionsCellItem
                    key={params.id}
                    id="download-btn"
                    label={t('global.download')}
                    icon={<DownloadIcon size={24} />}
                    showInMenu
                    onClick={() => onDownload(params)}
                  />
                ),
                onView && (
                  <GridActionsCellItem
                    key={params.id}
                    id="view-btn"
                    label={t('global.view')}
                    icon={<EyeIcon size={24} />}
                    showInMenu
                    onClick={() => onView(params)}
                  />
                ),
                hasEdit && (
                  <GridActionsCellItem
                    key={params.id}
                    id="edit-btn"
                    label={t('global.edit')}
                    icon={<EditIcon size={24} color={theme.palette.text.primary} />}
                    showInMenu
                    onClick={() => {
                      handleEdit(params.id);
                    }}
                  />
                ),
                onHide && (
                  <GridActionsCellItem
                    key={params.id}
                    id="hide-btn"
                    label={params.row.hidden ? t('global.show') : t('global.hide')}
                    icon={params.row.hidden ? <EyeIcon size={24} /> : <EyeSlashIcon size={24} />}
                    showInMenu
                    onClick={() => onHide(params)}
                  />
                ),
                onDelete && (
                  <GridActionsCellItem
                    key={params.id}
                    id="delete-btn"
                    label={t('global.delete')}
                    icon={<DeleteIcon size={24} color={theme.palette.error.main} />}
                    showInMenu
                    sx={{ color: theme.palette.error.main }}
                    onClick={() => onDelete(params)}
                  />
                ),
                onReset && (
                  <GridActionsCellItem
                    key={params.id}
                    id="reset-btn"
                    label={t('global.reset')}
                    icon={<ResetIcon size={24} />}
                    showInMenu
                    onClick={() => onReset(params)}
                  />
                ),
              ].filter(Boolean)
            : [],
      } as GridColDef)
    : undefined;

  const getBaseRowClassName = (params: GridRowClassNameParams) => {
    const baseStyle = params.id === selectedRowId ? 'single-selected' : '';

    const additionalStyle = getRowClassName?.(params) ?? '';

    return [additionalStyle, baseStyle].join(' ');
  };

  return (
    <Stack
      direction="column"
      gap={1}
      width="100%"
      height={!initialRows.length ? 264 : '100%'}
      maxHeight={640}
      sx={{ ..._container }}
    >
      <StyledDataGridPro
        ref={gridRef}
        apiRef={apiRef}
        editMode="row"
        columns={actionsColumn ? [...gridColumns, actionsColumn] : gridColumns}
        rows={rows}
        rowModesModel={rowModesModel}
        getRowId={getRowId}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        loading={loading || updating}
        slots={{
          columnSortedAscendingIcon: ChevronFilledUpIcon,
          columnSortedDescendingIcon: ChevronFilledDownIcon,
          moreActionsIcon: MoreActionsIcon,
          loadingOverlay: DataGridLoadingOverlay,
          noRowsOverlay: DataGridNoRowsOverlay,
          baseSelect: DataGridBaseSelect,
          ...slots,
        }}
        slotProps={{
          baseCheckbox: { size: 'small' },
          loadingOverlay: {
            isEmpty: !initialRows.length,
            isUpdating: updating,
            skeletonRows: 5,
          } as LoadingOverlayPropsOverrides,
          ...slotProps,
        }}
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        disableRowSelectionOnClick
        disableMultipleRowSelection
        hideFooter
        getRowClassName={getBaseRowClassName}
        rowHeight={GRID_ROW_HEIGHT}
        columnHeaderHeight={GRID_ROW_HEIGHT}
        sx={{
          '.MuiDataGrid-row.single-selected, .MuiDataGrid-row.single-selected:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.secondary.main,
          },
          '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '.MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
            backgroundColor: `${theme.palette.grey[400]} !important`,
          },
          ...sx,
        }}
        {...props}
      />
      {!readOnly && (
        <>
          {editingRowId ? (
            <Stack gap={1}>
              <LoadingButton
                loading={loading || updating}
                id="cancel-btn"
                color="secondary"
                onClick={handleCancel}
                sx={{ height: 38 }}
              >
                {t('global.cancel')}
              </LoadingButton>
              <LoadingButton loading={loading || updating} id="save-btn" onClick={handleSave} sx={{ height: 38 }}>
                {t('global.save')}
              </LoadingButton>
            </Stack>
          ) : (
            <>
              {onAdd && (
                <LoadingButton
                  id="add-btn"
                  loading={loading || updating}
                  onClick={handleAdd}
                  color="secondary"
                  startIcon={<AddIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {addLabel ?? t('global.add')}
                </LoadingButton>
              )}
            </>
          )}
        </>
      )}
    </Stack>
  );
};

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  ...GenericDataGridStyle(theme),
})) as typeof DataGridPro; // Casting required to resolve TypeScript issue: https://github.com/mui/mui-x/issues/6014

const editRowModesModel = (previousRowModesModel: GridRowModesModel, id: GridRowId, fieldToFocus?: string) => {
  return {
    ...Object.keys(previousRowModesModel).reduce<GridRowModesModel>(
      (acc, id) => ({ ...acc, [id]: { mode: GridRowModes.View, ignoreModifications: true } }),
      {},
    ),
    [id]: { mode: GridRowModes.Edit, fieldToFocus },
  };
};

const saveRowModesModel = (previousRowModesModel: GridRowModesModel) => {
  return Object.keys(previousRowModesModel).reduce<GridRowModesModel>(
    (acc, id) => ({ ...acc, [id]: { mode: GridRowModes.View } }),
    {},
  );
};

const cancelRowModesModel = (previousRowModesModel: GridRowModesModel) => {
  return Object.keys(previousRowModesModel).reduce<GridRowModesModel>(
    (acc, id) => ({ ...acc, [id]: { mode: GridRowModes.View, ignoreModifications: true } }),
    {},
  );
};

const MoreActionsIcon: FC = () => {
  return <MoreHorizontalIcon />;
};
